import { type ItemInstance, removeItemsFromParents, type TreeInstance } from "@headless-tree/core";
import { generateId } from "@msuite/utils/strings";
import type React from "react";
import { DragOverIndicator } from "./drag-over-indicator";
import type { TreeExternalApi, TreeItem, TreeItemApi } from "./tree";

export const TREE_INDENT = 30;

/** Props Interface */
interface TreeItemRowProps<T extends TreeItem<U>, U> {
	item: ItemInstance<T>;
	tree: TreeInstance<T>;
	data: Record<string, T>;
	render: (item: ItemInstance<T>, tree: TreeInstance<T>, api: TreeItemApi<T, U>) => React.ReactNode;
	append: TreeExternalApi<T, U>["append"];
	move: (item: ItemInstance<T>, direction: "up" | "down") => void;
	unindent: (item: ItemInstance<T>) => void;
	triggerOnChange: () => void;
}

export function TreeItemRow<T extends TreeItem<U>, U>({
	item,
	tree,
	data,
	render,
	append,
	move,
	unindent,
	triggerOnChange,
}: TreeItemRowProps<T, U>) {
	/** Functions */
	function onRemove() {
		const id = item.getId();
		if (!data[id]) return;
		removeItemsFromParents([item], (item, newChildren) => {
			item.getItemData().children = newChildren;
		}).then(() => {
			delete data[id];
		});
		triggerOnChange();
	}

	function onDuplicate() {
		const originalId = item.getId();
		if (!data[originalId]) return;
		function cloneSubtree(sourceId: string): string {
			const source = data[sourceId] as T;
			const newId = generateId();
			const clonedData = source?.data ? (JSON.parse(JSON.stringify(source.data)) as U) : undefined;
			data[newId] = { id: newId, data: clonedData as any, children: [] } as any;
			const newNode = data[newId]!;
			for (const childId of source.children || []) {
				const newChildId = cloneSubtree(childId);
				(newNode.children as string[]).push(newChildId);
			}
			return newId;
		}

		const parent = item.getParent();
		const parentId = parent?.getId() || "root";
		if (!data[parentId]) return;
		const cloneRootId = cloneSubtree(originalId);
		// Insert as next sibling after the original
		const siblings = data[parentId].children || [];
		const idx = siblings.indexOf(originalId);
		if (idx === -1) {
			data[parentId].children = [...siblings, cloneRootId] as any;
		} else {
			const next = [...siblings];
			next.splice(idx + 1, 0, cloneRootId);
			data[parentId].children = next as any;
		}
		parent?.expand();
		triggerOnChange();
	}

	/** Computed */
	const ITEM_INDENT = item.getItemMeta().level * TREE_INDENT;
	const draggedItems = tree.getState().dnd?.draggedItems;
	const isBeingDragged = !!draggedItems?.some(
		(draggedItem) => draggedItem.getId() === item.getId(),
	);
	// Never mount the capture overlay onto the row currently being dragged: that DOM
	// mutation happens (via the re-render dragstart itself triggers) at the exact
	// instant the cursor is still over this row, and inserting a new element right
	// there disrupts the browser's native drag tracking before any dragover can fire.
	const isAnyDragActive = !!draggedItems && !isBeingDragged;
	const rawDragHandleProps = item.getDragHandleProps();
	const dragHandleProps = {
		...rawDragHandleProps,
		onDragEnd: (e: React.DragEvent) => {
			rawDragHandleProps.onDragEnd?.(e);
			// headless-tree's own onDragEnd bails out early for a cancelled drag
			// (dropEffect "none", e.g. dropped outside any valid target), leaving
			// draggedItems stuck forever — which would permanently keep the drag
			// capture overlay below shown, blocking every future drag. Always clear it.
			tree.applySubStateUpdate("dnd", null);
		},
	};

	/** Render */
	return (
		<div
			className="relative"
			{...item.getProps()}
			style={{
				paddingLeft: ITEM_INDENT,
			}}
		>
			{item.isDragTarget() && !item.isDragTargetBelow() && !item.isDragTargetAbove() && (
				<div
					className="absolute inset-0"
					style={{
						left: ITEM_INDENT,
					}}
				>
					<DragOverIndicator />
				</div>
			)}
			{render(item, tree, {
				onRemove,
				onDuplicate,
				append,
				moveDown: () => move(item, "down"),
				moveUp: () => move(item, "up"),
				unindent: () => unindent(item),
				dragHandleProps,
			})}
			{isAnyDragActive && (
				// Rich text editors and other interactive children have their own dragover
				// handling (e.g. ProseMirror's drop-cursor) that fights ours and causes the
				// drag-over highlight to flicker. Capture drag events above all row content
				// for the duration of any drag so they only ever reach our own handlers.
				<div className="absolute inset-0 z-40" />
			)}
		</div>
	);
}
